'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import moment from 'moment-timezone'
import { Calendar } from '@/app/components/booking/Calendar'
import { AddressInput, AddressData } from '@/app/components/common/AddressInput'
import { ContactForm } from '@/app/components/booking/ContactForm'
import { Spinner } from '@/app/components/common/Spinner'
import { TimezoneCombobox } from '@/app/components/common/TimezoneCombobox'
import { HomeIcon, VideoCameraIcon } from '@heroicons/react/24/outline'
import { logError, debug } from '@/lib/logging'

interface CalendarPickerProps {
  providerId: string
  /** Multiple provider profile IDs for shared consults; when set, availability is intersected across all providers */
  providerIds?: string[]
  providerName: string
  providerEmail?: string
  orgId?: string
  appointmentTypeId?: string
  onSlotSelected?: (
    slot: { start: string; end: string; available?: boolean; inAvailabilityWindow?: boolean },
    timezone: string
  ) => void
  /** Called when the user changes the selected duration */
  onDurationChange?: (durationMinutes: number) => void
  packageId?: string
  paymentId?: string
  hideBookingSection?: boolean
  /** Booking mode passed from parent; overrides internal selectedMode for availability queries */
  mode?: 'in-home' | 'video'
  /** When true, all slots are clickable regardless of availability status (admin/provider use) */
  allowBusySlots?: boolean
  /** When true, past dates are selectable and availability is fetched for past months (admin/provider use) */
  allowPastDates?: boolean
  /** Client address to pre-populate when booking on behalf of a client (skips /api/user/profile fetch) */
  clientAddress?: Partial<AddressData>
}

interface TimeSlot {
  start: string
  end: string
  available: boolean
  inAvailabilityWindow?: boolean
}

interface PackageData {
  id: string
  name: string
  modes?: string[]
  durationMinutes?: number
}

interface MonthCache {
  [key: string]: {
    slotsByDate: Record<string, TimeSlot[]>
    availableDates: Set<string>
    fetchedAt: number
  }
}

export function CalendarPicker({
  providerId,
  providerIds,
  providerName,
  providerEmail,
  orgId,
  appointmentTypeId,
  onSlotSelected,
  onDurationChange,
  packageId,
  paymentId,
  hideBookingSection = false,
  mode: modeProp,
  allowBusySlots = false,
  allowPastDates = false,
  clientAddress,
}: CalendarPickerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null)
  const [allSlots, setAllSlots] = useState<TimeSlot[]>([])
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set())
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [loadingDates, setLoadingDates] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [monthCache, setMonthCache] = useState<MonthCache>({})
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [slotDuration, _setSlotDuration] = useState<number>(30) // Default to 30 minutes
  const [isBooking, setIsBooking] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [showContactFormFallback, setShowContactFormFallback] = useState(false)

  // Duration range state (populated from appointment type)
  const [minDurationMinutes, setMinDurationMinutes] = useState<number | null>(null)
  const [maxDurationMinutes, setMaxDurationMinutes] = useState<number | null>(null)
  const [selectedDurationMinutes, setSelectedDurationMinutes] = useState<number | null>(null)

  // Mode selection and address state
  const [packageData, _setPackageData] = useState<PackageData | null>(null)
  const [selectedMode, setSelectedMode] = useState<'in-home' | 'video'>('video')
  const [address, setAddress] = useState<Partial<AddressData>>({})
  const [addressErrors, setAddressErrors] = useState<Partial<Record<keyof AddressData, string>>>({})
  const [, setLoadingPackage] = useState(false)
  const [, setLoadingUserData] = useState(false)
  const [timezone, setTimezone] = useState<string>(() => moment.tz.guess())

  // Calculate min date for calendar (no max - availability data speaks for itself)
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)
  const calendarMinDate = allowPastDates ? undefined : tomorrow

  const getMonthKey = useCallback(
    (date: Date) => {
      const effectiveMode = modeProp || selectedMode
      return `${date.getFullYear()}-${date.getMonth() + 1}-${effectiveMode}-${timezone}-${selectedDurationMinutes ?? 'default'}`
    },
    [modeProp, selectedMode, timezone, selectedDurationMinutes]
  )

  // Fetch appointment type data if appointmentTypeId is provided
  useEffect(() => {
    if (!appointmentTypeId) return

    const fetchAppointmentType = async () => {
      try {
        setLoadingPackage(true)
        debug('ui', `Fetching appointment type data for ${appointmentTypeId}`)

        const response = await fetch(`/api/appointment-types/${appointmentTypeId}`)
        if (!response.ok) {
          throw new Error('Failed to fetch appointment type data')
        }
        const data = await response.json()
        _setPackageData({
          id: data.id,
          name: data.name,
          modes: data.modes || [],
          durationMinutes: data.appointmentDurationHours
            ? data.appointmentDurationHours * 60
            : undefined,
        })
        if (data.modes && data.modes.length > 0) {
          setSelectedMode(data.modes[0] as 'in-home' | 'video')
        }

        // Set up duration range if min/max differ from default
        const defaultMinutes = data.appointmentDurationHours
          ? data.appointmentDurationHours * 60
          : null
        const min = data.minDurationHours ? data.minDurationHours * 60 : defaultMinutes
        const max = data.maxDurationHours ? data.maxDurationHours * 60 : defaultMinutes
        setMinDurationMinutes(min)
        setMaxDurationMinutes(max)
        setSelectedDurationMinutes(defaultMinutes)
        if (defaultMinutes) onDurationChange?.(defaultMinutes)

        debug(
          'ui',
          `Loaded appointment type: ${data.name}, modes: ${data.modes?.join(', ')}, duration range: ${min}-${max}min`
        )
      } catch (error) {
        logError('Failed to fetch appointment type data:', error)
      } finally {
        setLoadingPackage(false)
      }
    }

    fetchAppointmentType()
  }, [appointmentTypeId])

  // Fetch package data if packageId is provided
  useEffect(() => {
    if (!packageId || !orgId) return

    const fetchPackage = async () => {
      try {
        setLoadingPackage(true)
        debug('ui', `Fetching package data for ${packageId}`)

        // TODO: The /api/booking/packages endpoint does not exist.
        // Packages should be passed as props or fetched from /api/admin/packages
        // Skipping package fetch for now to prevent 404 errors
        debug('ui', 'Package fetching disabled - endpoint not implemented')
        setLoadingPackage(false)
        return

        // Original non-functional code (commented out):
        // const response = await fetch(
        //   `/api/booking/packages?providerId=${providerId}&orgId=${orgId}`
        // )
        // if (!response.ok) {
        //   throw new Error('Failed to fetch package data')
        // }
        // const data = await response.json()
        // const pkg = data.packages?.find((p: PackageData) => p.id === packageId)
        // if (pkg) {
        //   setPackageData(pkg)
        //   if (pkg.modes && pkg.modes.length > 0) {
        //     setSelectedMode(pkg.modes[0] as 'in-home' | 'video')
        //   }
        //   debug('ui', `Loaded package: ${pkg.name}, modes: ${pkg.modes?.join(', ')}`)
        // }
      } catch (error) {
        logError('Failed to fetch package data:', error)
        setLoadingPackage(false)
      } finally {
        setLoadingPackage(false)
      }
    }

    fetchPackage()
  }, [packageId, providerId, orgId])

  // Fetch user profile data to pre-populate address
  useEffect(() => {
    if (!paymentId) return // Only for package appointments

    // If client address is provided (admin booking on behalf of client), use it directly
    if (clientAddress) {
      if (
        clientAddress.address1 ||
        clientAddress.city ||
        clientAddress.state ||
        clientAddress.zipCode
      ) {
        setAddress({
          address1: clientAddress.address1 || '',
          address2: clientAddress.address2 || '',
          city: clientAddress.city || '',
          state: clientAddress.state || '',
          zipCode: clientAddress.zipCode || '',
        })
        debug('ui', 'Pre-populated address from client profile')
      }
      return
    }

    const fetchUserData = async () => {
      try {
        setLoadingUserData(true)
        debug('ui', 'Fetching user profile data for address pre-population')

        const response = await fetch('/api/user/profile')
        if (!response.ok) {
          debug('ui', 'User profile not available')
          return
        }

        const userData = await response.json()
        // Pre-populate any available address fields
        if (userData.address1 || userData.city || userData.state || userData.zipCode) {
          setAddress({
            address1: userData.address1 || '',
            address2: userData.address2 || '',
            city: userData.city || '',
            state: userData.state || '',
            zipCode: userData.zipCode || '',
          })
          debug('ui', 'Pre-populated address from user profile')
        }
      } catch (error) {
        logError('Failed to fetch user data:', error)
      } finally {
        setLoadingUserData(false)
      }
    }

    fetchUserData()
  }, [paymentId, clientAddress])

  // Track in-flight requests to prevent duplicates - use refs to avoid dependency issues
  const requestsInFlightRef = useRef<Set<string>>(new Set())
  const lastRequestTimeRef = useRef<Map<string, number>>(new Map())
  const MIN_REQUEST_INTERVAL = 2000 // Minimum 2 seconds between requests for the same month

  // Retry utility with exponential backoff
  const _fetchWithRetry = async (
    url: string,
    maxRetries = 5,
    baseDelay = 1000
  ): Promise<Response> => {
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        debug('ui', `Fetch attempt ${attempt + 1}/${maxRetries + 1}: ${url}`)
        const response = await fetch(url)

        // Don't retry on certain status codes
        if (response.ok || response.status === 404 || response.status === 403) {
          return response
        }

        // For rate limiting, wait longer
        if (response.status === 429) {
          if (attempt < maxRetries) {
            const delay = baseDelay * Math.pow(2, attempt) * 2 // Double delay for rate limits
            debug('ui', `Rate limited, waiting ${delay}ms before retry ${attempt + 1}`)
            await new Promise((resolve) => setTimeout(resolve, delay))
            continue
          }
        }

        // For 5xx errors, retry
        if (response.status >= 500 && attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt)
          debug(
            'ui',
            `Server error (${response.status}), waiting ${delay}ms before retry ${attempt + 1}`
          )
          await new Promise((resolve) => setTimeout(resolve, delay))
          continue
        }

        // For 4xx errors (except 429), don't retry but return the response
        if (response.status >= 400 && response.status < 500) {
          return response
        }

        // If we get here, return the response
        return response
      } catch (error) {
        logError('Network error during fetch:', error)
        lastError = error instanceof Error ? error : new Error(String(error))
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt)
          debug(
            'ui',
            `Network error, waiting ${delay}ms before retry ${attempt + 1}: ${lastError.message}`
          )
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }
    }

    throw lastError || new Error('Failed to fetch after all retries')
  }

  // Single effect to handle month data fetching and caching
  useEffect(() => {
    const monthKey = getMonthKey(currentMonth)
    const cached = monthCache[monthKey]

    if (cached) {
      // Use cached data
      debug('ui', `Using cached data for ${monthKey}`)
      setAvailableDates(cached.availableDates)
      setLoadingDates(false)
      return
    }

    if (requestsInFlightRef.current.has(monthKey)) {
      debug('ui', `Request already in flight for ${monthKey}, skipping`)
      return
    }

    // Check if we're making requests too frequently
    const lastRequest = lastRequestTimeRef.current.get(monthKey) || 0
    const timeSinceLastRequest = Date.now() - lastRequest
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      debug(
        'ui',
        `Rate limiting: waiting ${MIN_REQUEST_INTERVAL - timeSinceLastRequest}ms before requesting ${monthKey}`
      )
      return
    }

    // Fetch new data
    const fetchData = async () => {
      const _year = currentMonth.getFullYear()
      const _monthNum = currentMonth.getMonth() + 1

      debug('ui', `Fetching availability for month ${monthKey}`)
      setLoadingDates(true)
      setFetchError(null)
      requestsInFlightRef.current.add(monthKey)
      lastRequestTimeRef.current.set(monthKey, Date.now())

      const _errorWasSet = false

      try {
        // Get user's timezone for availability calculation
        const userTimezone = timezone

        // Build URL - appointmentTypeId is optional for free consultations
        const effectiveMode = modeProp || selectedMode
        const durationParam = selectedDurationMinutes
          ? `&durationMinutes=${selectedDurationMinutes}`
          : ''
        // Use multi-provider endpoint when multiple providers are assigned
        const providerParam =
          providerIds && providerIds.length > 1
            ? `providerIds=${encodeURIComponent(providerIds.join(','))}`
            : `providerId=${providerId}`
        const url = `/api/availability?${providerParam}${appointmentTypeId ? `&appointmentTypeId=${appointmentTypeId}` : ''}&month=${_monthNum}&year=${_year}&timezone=${encodeURIComponent(userTimezone)}&mode=${effectiveMode}${durationParam}${allowPastDates ? '&allowPast=true' : ''}${allowBusySlots ? '&includeOutsideAvailability=true' : ''}`
        const response = await _fetchWithRetry(url)

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          const errorMessage =
            errorData.error || `HTTP ${response.status}: Failed to fetch availability`

          // Check if this is the "free consultation not configured" error
          if (errorMessage.includes('Provider has not configured free consultation availability')) {
            // Show contact form instead of calendar
            setShowContactFormFallback(true)
            setLoadingDates(false)
            requestsInFlightRef.current.delete(monthKey)
            return
          }

          if (response.status === 429) {
            setFetchError('Too many requests. Please wait a moment and try again.')
          } else if (response.status === 404) {
            setFetchError('Provider not found. Please check the provider information.')
          } else if (response.status === 400) {
            setFetchError(
              errorMessage.includes('Organization ID')
                ? 'Invalid organization information. Please contact support.'
                : 'Unable to load calendar availability. Please try again or contact support.'
            )
          } else if (response.status >= 500) {
            setFetchError('Server error. Please try again in a few moments.')
          } else {
            setFetchError('Unable to load calendar availability. Please try again later.')
          }
          throw new Error(errorMessage)
        }

        const data = await response.json()
        const slotsByDate = data.slotsByDate || {}
        if (data.slotDurationMinutes) {
          _setSlotDuration(data.slotDurationMinutes)
        }
        // Only mark dates as available if they have at least one available slot
        // (or all dates with any slots when allowBusySlots is true)
        const availableDates = new Set<string>(
          Object.entries(slotsByDate)
            .filter(([, slots]) =>
              allowBusySlots
                ? (slots as TimeSlot[]).length > 0
                : (slots as TimeSlot[]).some((slot: TimeSlot) => slot.available)
            )
            .map(([date]) => date)
        )

        const monthData = {
          slotsByDate,
          availableDates,
          fetchedAt: Date.now(),
        }
        setMonthCache((prev) => ({
          ...prev,
          [monthKey]: monthData,
        }))
        setAvailableDates(availableDates)
        setFetchError(null)
        debug('ui', `Cached ${availableDates.size} available dates for ${monthKey}`)
      } catch (err) {
        logError(`Failed to fetch availability for month ${monthKey} after retries:`, err)
        // If no error message was set yet (network error case), set a generic one
        if (!_errorWasSet) {
          setFetchError('Network error. Please check your internet connection and try again.')
        }
      } finally {
        setLoadingDates(false)
        requestsInFlightRef.current.delete(monthKey)
      }
    }

    fetchData()
  }, [
    currentMonth,
    providerId,
    // Re-fetch availability when the set of selected providers changes (multi-provider intersection)
    providerIds?.join(','),
    appointmentTypeId,
    getMonthKey,
    modeProp,
    selectedMode,
    allowBusySlots,
    selectedDurationMinutes,
  ]) // appointmentTypeId is optional, mode triggers re-fetch for travel buffer

  // Handle month changes from Calendar component
  const handleMonthChange = useCallback((newMonth: Date) => {
    setCurrentMonth(newMonth)
    // Clear selected date and slots when changing months for better UX
    setSelectedDate(null)
    setSelectedSlot(null)
    setAllSlots([])
    // The useEffect above will handle fetching/caching logic
  }, [])

  // Get slots for selected date from cache
  useEffect(() => {
    if (!selectedDate) {
      setAllSlots([])
      setSelectedSlot(null)
      return
    }

    setLoadingSlots(true)
    setError(null)

    const dateStr = selectedDate.toISOString().split('T')[0]
    const monthKey = getMonthKey(selectedDate)
    const cached = monthCache[monthKey]

    if (cached && cached.slotsByDate[dateStr]) {
      // We have cached slots for this date - use them directly
      // API returns all slots within the provider's configured windows with availability status
      const slots = cached.slotsByDate[dateStr]

      setAllSlots(slots)
      setLoadingSlots(false)
      const availableCount = slots.filter((s: TimeSlot) => s.available).length
      debug(
        'ui',
        `Loaded ${slots.length} slots for ${dateStr} (${availableCount} available, ${slots.length - availableCount} unavailable)`
      )
    } else {
      // No cached data for this date - show empty state
      setAllSlots([])
      setLoadingSlots(false)
      debug('ui', `No cached slots for ${dateStr}, showing empty`)
    }
  }, [selectedDate, monthCache, getMonthKey])

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setSelectedSlot(null) // Reset slot selection when date changes
  }

  const handleSlotClick = (slot: TimeSlot) => {
    setSelectedSlot(slot)
    if (onSlotSelected) {
      onSlotSelected(slot, timezone)
    }
  }

  const validateAddress = () => {
    const errors: Partial<Record<keyof AddressData, string>> = {}

    if (!address.address1?.trim()) {
      errors.address1 = 'Street address is required'
    }
    if (!address.city?.trim()) {
      errors.city = 'City is required'
    }
    if (!address.state?.trim()) {
      errors.state = 'State is required'
    } else if (address.state.length !== 2) {
      errors.state = 'State must be 2 letters (e.g., NY)'
    }
    if (!address.zipCode?.trim()) {
      errors.zipCode = 'ZIP code is required'
    }

    setAddressErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleBookAppointment = async () => {
    if (!selectedDate || !selectedSlot) {
      setError('Please select a date and time')
      return
    }

    // Validate address if in-person mode is selected
    if (paymentId && selectedMode === 'in-home') {
      if (!validateAddress()) {
        setError('Please complete the address form for in-person appointments')
        return
      }
    }

    // For free consultations without payment ID, redirect to contact form with selected time
    if (!paymentId) {
      const params = new URLSearchParams({
        slotStart: selectedSlot.start,
        slotEnd: selectedSlot.end,
        providerId,
        providerName,
        timezone,
      })
      if (orgId) params.set('orgId', orgId)

      // Navigate to contact form with pre-selected time
      window.location.href = `/booking/contact?${params.toString()}`
      return
    }

    // For post-purchase bookings with existing user session, proceed directly
    setIsBooking(true)

    try {
      // First, get the current user's profile information
      const userResponse = await fetch('/api/user/profile')
      if (!userResponse.ok) {
        throw new Error('Failed to get user profile information')
      }
      const userProfile = await userResponse.json()

      const appointmentData: any = {
        name: userProfile.name || 'Unknown User',
        email: userProfile.email || '',
        phone: userProfile.phone || '555-123-4567', // Fallback phone for testing
        providerId,
        orgId,
        slotStart: selectedSlot.start,
        slotEnd: selectedSlot.end,
        paymentId,
        mode: selectedMode,
        notes: 'Post-purchase appointment booking',
      }

      // Add address fields if in-person mode
      if (selectedMode === 'in-home') {
        appointmentData.address1 = address.address1
        appointmentData.address2 = address.address2
        appointmentData.city = address.city
        appointmentData.state = address.state
        appointmentData.zipCode = address.zipCode
      }

      const response = await fetch('/api/appointments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to book appointment')
      }

      // Redirect to dashboard to see the appointment
      window.location.href = '/dashboard'
    } catch (error) {
      logError('Failed to book appointment:', error)
      setError(
        error instanceof Error ? error.message : 'Failed to book appointment. Please try again.'
      )
    } finally {
      setIsBooking(false)
    }
  }

  const formatTime = (isoString: string) => {
    return moment.tz(isoString, timezone).format('h:mm A')
  }

  const formatSelectedDate = () => {
    if (!selectedDate) return ''
    if (selectedSlot) {
      return moment.tz(selectedSlot.start, timezone).format('dddd, MMMM D, YYYY')
    }
    return moment.tz(selectedDate, timezone).format('dddd, MMMM D, YYYY')
  }

  const handleTimezoneChange = useCallback((tz: string) => {
    setTimezone(tz)
    setMonthCache({})
  }, [])

  const handleDurationChange = useCallback(
    (minutes: number) => {
      setSelectedDurationMinutes(minutes)
      setMonthCache({})
      setSelectedDate(null)
      setSelectedSlot(null)
      setAllSlots([])
      onDurationChange?.(minutes)
    },
    [onDurationChange]
  )

  // If provider hasn't configured free consultation availability, show contact form
  if (showContactFormFallback) {
    return (
      <div data-component-name="CalendarPicker">
        <ContactForm
          providerId={providerId}
          providerName={providerName}
          providerEmail={providerEmail || ''}
          orgId={orgId}
          consultationType="free"
          onBack={() => {
            setShowContactFormFallback(false)
            setFetchError(null)
            setMonthCache({})
          }}
        />
      </div>
    )
  }

  return (
    <div data-component-name="CalendarPicker" className="space-y-6">
      {/* Package Mode Selection */}
      {packageData && packageData.modes && packageData.modes.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Appointment Type</h3>

          {packageData.modes.length === 1 ? (
            // Single mode - display only
            <div className="flex items-center space-x-2 text-gray-700">
              {packageData.modes[0] === 'in-home' ? (
                <>
                  <HomeIcon className="w-5 h-5 text-primary" />
                  <span className="text-sm">In-Person Visit</span>
                </>
              ) : (
                <>
                  <VideoCameraIcon className="w-5 h-5 text-primary" />
                  <span className="text-sm">Virtual Call (Google Meet)</span>
                </>
              )}
            </div>
          ) : (
            // Multiple modes - show radio selection
            <div className="space-y-3">
              {packageData.modes.map((mode) => (
                <label
                  key={mode}
                  className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedMode === mode
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="appointmentMode"
                    value={mode}
                    checked={selectedMode === mode}
                    onChange={(e) => {
                      setSelectedMode(e.target.value as 'in-home' | 'video')
                      setAddressErrors({}) // Clear address errors when changing mode
                    }}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  {mode === 'in-home' ? (
                    <>
                      <HomeIcon className="w-5 h-5 text-gray-600" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">In-Person Visit</span>
                        <p className="text-xs text-gray-500">Doula will come to your location</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <VideoCameraIcon className="w-5 h-5 text-gray-600" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">
                          Virtual (Call/Video)
                        </span>
                        <p className="text-xs text-gray-500">Google Meet link will be provided</p>
                      </div>
                    </>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Duration Range Selector */}
      {minDurationMinutes !== null &&
        maxDurationMinutes !== null &&
        minDurationMinutes !== maxDurationMinutes && (
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Session Duration</h3>
            <select
              value={selectedDurationMinutes ?? ''}
              onChange={(e) => handleDurationChange(parseInt(e.target.value))}
              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {Array.from(
                { length: Math.floor((maxDurationMinutes - minDurationMinutes) / 15) + 1 },
                (_, i) => minDurationMinutes + i * 15
              ).map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes < 60
                    ? `${minutes} min`
                    : minutes % 60 === 0
                      ? `${minutes / 60} hr`
                      : `${Math.floor(minutes / 60)} hr ${minutes % 60} min`}
                </option>
              ))}
            </select>
          </div>
        )}

      {/* Address Input for In-Person Mode - only shown for standalone booking (not when embedded in BookingModal) */}
      {paymentId && selectedMode === 'in-home' && !hideBookingSection && (
        <AddressInput
          address={address}
          onChange={setAddress}
          errors={addressErrors}
          label="Appointment Address"
          required
        />
      )}

      {slotDuration !== 30 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            <strong>Session Duration:</strong> {slotDuration} minutes
          </p>
        </div>
      )}

      {/* Error Display */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Unable to load calendar</h3>
              <p className="mt-1 text-sm text-red-700">{fetchError}</p>
              <button
                onClick={() => {
                  setFetchError(null)
                  setMonthCache({})
                  setCurrentMonth(new Date(currentMonth))
                }}
                className="mt-3 text-sm font-medium text-red-800 hover:text-red-900 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Select a Date</label>
        {loadingDates ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : fetchError ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <p className="text-sm">Please resolve the error above to continue</p>
          </div>
        ) : (
          <Calendar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onMonthChange={handleMonthChange}
            availableDates={availableDates}
            minDate={calendarMinDate}
            currentMonth={currentMonth}
          />
        )}
        {selectedDate && (
          <p className="text-sm text-gray-600 mt-3 text-center">{formatSelectedDate()}</p>
        )}
      </div>

      {/* Timezone Selector */}
      {selectedDate && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-3 flex items-center gap-2.5 flex-wrap">
          <span className="text-sm text-sky-900">🌐 Times shown in:</span>
          <TimezoneCombobox value={timezone} onChange={handleTimezoneChange} />
        </div>
      )}

      {/* Time Slots */}
      {selectedDate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Available Times</label>

          {error && <div className="p-4 bg-red-50 text-red-700 rounded-lg mb-4">{error}</div>}

          {/* Legend (admin/provider mode only) */}
          {allowBusySlots && allSlots.length > 0 && !loadingSlots && (
            <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-600">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded bg-green-50 border border-green-300" />
                In availability &middot; Free
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-300" />
                In availability &middot; Busy
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded bg-gray-100 border border-gray-300" />
                Outside availability
              </span>
            </div>
          )}

          {/* Outside availability warning */}
          {allowBusySlots && selectedSlot && selectedSlot.inAvailabilityWindow === false && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3 text-sm text-amber-800">
              This appointment is outside the provider&apos;s configured availability. Confirm with
              the provider before scheduling.
            </div>
          )}

          <div className="relative">
            {/* Show spinner overlay while loading */}
            {loadingSlots && (
              <div className="absolute inset-0 bg-white/10 flex items-center justify-center z-10 rounded-lg">
                <Spinner size="lg" />
              </div>
            )}

            {/* Always show slots if we have them (either all slots or just a message) */}
            {allSlots.length > 0 ? (
              <div
                className={`grid gap-2 ${allowBusySlots ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'}`}
              >
                {allSlots.map((slot, index) => (
                  <button
                    key={index}
                    onClick={() => (slot.available || allowBusySlots) && handleSlotClick(slot)}
                    disabled={(!slot.available && !allowBusySlots) || loadingSlots}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                      selectedSlot?.start === slot.start
                        ? 'bg-primary text-white border-primary'
                        : allowBusySlots
                          ? slot.inAvailabilityWindow === false
                            ? 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                            : slot.available
                              ? 'bg-green-50 border-green-300 text-gray-700 hover:bg-green-100'
                              : 'bg-red-50 border-red-300 text-gray-700 hover:bg-red-100'
                          : slot.available
                            ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                    } ${loadingSlots ? 'pointer-events-none' : ''}`}
                    type="button"
                  >
                    <span className="block">{formatTime(slot.start)}</span>
                    {allowBusySlots && (
                      <span className="block text-xs font-normal opacity-75">
                        {slot.available ? 'Free' : 'Busy'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              // Show empty state only when we have no slots at all
              !loadingSlots && (
                <div className="p-4 bg-gray-50 text-gray-600 rounded-lg">
                  No available times on this date. Please select another date.
                </div>
              )
            )}

            {/* Show skeleton on initial load when no slots exist yet */}
            {loadingSlots && allSlots.length === 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {[...Array(16)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected Time Display */}
      {selectedSlot && !hideBookingSection && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800 mb-3">
            <strong>Selected time:</strong> {formatTime(selectedSlot.start)} -{' '}
            {formatTime(selectedSlot.end)} on {formatSelectedDate()}
          </p>
          <button
            type="button"
            onClick={handleBookAppointment}
            disabled={isBooking}
            className="w-full bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-hover transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isBooking ? (
              <>
                <Spinner size="sm" color="text-white" className="mr-2" />
                Booking...
              </>
            ) : (
              'Book Appointment'
            )}
          </button>
        </div>
      )}

      {/* Hidden fields for form submission */}
      <input
        type="hidden"
        name="selectedDate"
        value={selectedDate?.toISOString().split('T')[0] || ''}
      />
      <input type="hidden" name="selectedSlotStart" value={selectedSlot?.start || ''} />
      <input type="hidden" name="selectedSlotEnd" value={selectedSlot?.end || ''} />
      <input type="hidden" name="selectedTimezone" value={timezone} />
    </div>
  )
}
